<template>
  <v-container fluid class="list-detail-container">
    <v-row v-if="!loading && list">
      <!-- Header -->
      <v-col cols="12">
        <v-card class="mb-4" :style="{ borderTop: `4px solid ${list.color}` }">
          <v-card-title class="d-flex align-center">
            <v-btn
              icon
              @click="$router.back()"
              class="mr-2"
            >
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <v-icon :color="list.color" class="mr-2">{{ list.icon }}</v-icon>
            <span>{{ list.name }}</span>
            <v-spacer></v-spacer>
            <v-btn
              v-if="permissions.can_create"
              color="success"
              prepend-icon="mdi-plus"
              @click="openAddDialog"
              class="mr-2"
            >
              Neuer Eintrag
            </v-btn>
            <v-btn
              v-if="permissions.can_export"
              color="primary"
              prepend-icon="mdi-download"
              @click="exportToExcel"
              :loading="exporting"
            >
              CSV Export
            </v-btn>
          </v-card-title>
          <v-card-subtitle v-if="list.description">
            {{ list.description }}
          </v-card-subtitle>
        </v-card>
      </v-col>

      <!-- Daten-Tabelle -->
      <v-col cols="12">
        <v-card>
          <v-card-title>
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              label="Suchen..."
              single-line
              hide-details
              clearable
            ></v-text-field>
          </v-card-title>

          <v-data-table
            :headers="tableHeaders"
            :items="tableData"
            :search="search"
            class="elevation-1"
            items-per-page="25"
            :items-per-page-options="[10, 25, 50, 100]"
          >
            <!-- Dynamische Spalten -->
            <template
              v-for="column in columns"
              :key="column.column_name"
              #[`item.${column.column_name}`]="{ item }"
            >
              <div v-if="column.column_type === 'checkbox'">
                <v-icon :color="item[column.column_name] ? 'success' : 'grey'">
                  {{ item[column.column_name] ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                </v-icon>
              </div>
              <v-chip
                v-else-if="column.column_type === 'select'"
                size="small"
                color="primary"
              >
                {{ item[column.column_name] }}
              </v-chip>
              <div v-else-if="column.column_type === 'multiselect'">
                <v-chip
                  v-for="(val, idx) in (item[column.column_name] || [])"
                  :key="idx"
                  size="small"
                  class="mr-1"
                >
                  {{ val }}
                </v-chip>
              </div>
              <span v-else>{{ item[column.column_name] }}</span>
            </template>

            <!-- Aktionen -->
            <template #item.actions="{ item }">
              <v-btn
                v-if="permissions.can_edit"
                icon
                size="small"
                @click="editItem(item)"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn
                v-if="permissions.can_delete"
                icon
                size="small"
                color="error"
                @click="deleteItem(item)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Loading -->
    <v-row v-if="loading">
      <v-col class="text-center py-12">
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
        ></v-progress-circular>
      </v-col>
    </v-row>

    <!-- Dialog: Eintrag hinzufügen/bearbeiten -->
    <v-dialog v-model="dataDialog" max-width="800px" scrollable>
      <v-card>
        <v-card-title class="bg-primary">
          <v-icon class="mr-2">{{ editingItem ? 'mdi-pencil' : 'mdi-plus' }}</v-icon>
          {{ editingItem ? 'Eintrag bearbeiten' : 'Neuer Eintrag' }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-form ref="dataForm">
            <v-row>
              <v-col
                v-for="column in columns"
                :key="column.id"
                cols="12"
                :md="getColumnWidth(column)"
              >
                <!-- Text -->
                <v-text-field
                  v-if="column.column_type === 'text'"
                  v-model="currentData[column.column_name]"
                  :label="column.column_name"
                  :required="column.is_required"
                  :rules="column.is_required ? [v => !!v || 'Pflichtfeld'] : []"
                  :hint="column.help_text"
                  persistent-hint
                ></v-text-field>

                <!-- Textarea -->
                <v-textarea
                  v-else-if="column.column_type === 'textarea'"
                  v-model="currentData[column.column_name]"
                  :label="column.column_name"
                  :required="column.is_required"
                  :rules="column.is_required ? [v => !!v || 'Pflichtfeld'] : []"
                  :hint="column.help_text"
                  persistent-hint
                  rows="3"
                ></v-textarea>

                <!-- Number -->
                <v-text-field
                  v-else-if="column.column_type === 'number'"
                  v-model.number="currentData[column.column_name]"
                  :label="column.column_name"
                  type="number"
                  :required="column.is_required"
                  :rules="column.is_required ? [v => v !== null && v !== '' || 'Pflichtfeld'] : []"
                  :hint="column.help_text"
                  persistent-hint
                ></v-text-field>

                <!-- Date -->
                <v-text-field
                  v-else-if="column.column_type === 'date'"
                  v-model="currentData[column.column_name]"
                  :label="column.column_name"
                  type="date"
                  :required="column.is_required"
                  :rules="column.is_required ? [v => !!v || 'Pflichtfeld'] : []"
                  :hint="column.help_text"
                  persistent-hint
                ></v-text-field>

                <!-- DateTime -->
                <v-text-field
                  v-else-if="column.column_type === 'datetime'"
                  v-model="currentData[column.column_name]"
                  :label="column.column_name"
                  type="datetime-local"
                  :required="column.is_required"
                  :rules="column.is_required ? [v => !!v || 'Pflichtfeld'] : []"
                  :hint="column.help_text"
                  persistent-hint
                ></v-text-field>

                <!-- Select -->
                <v-select
                  v-else-if="column.column_type === 'select'"
                  v-model="currentData[column.column_name]"
                  :label="column.column_name"
                  :items="column.options || []"
                  :required="column.is_required"
                  :rules="column.is_required ? [v => !!v || 'Pflichtfeld'] : []"
                  :hint="column.help_text"
                  persistent-hint
                ></v-select>

                <!-- Multiselect -->
                <v-select
                  v-else-if="column.column_type === 'multiselect'"
                  v-model="currentData[column.column_name]"
                  :label="column.column_name"
                  :items="column.options || []"
                  multiple
                  chips
                  :required="column.is_required"
                  :rules="column.is_required ? [v => (v && v.length > 0) || 'Pflichtfeld'] : []"
                  :hint="column.help_text"
                  persistent-hint
                ></v-select>

                <!-- Checkbox -->
                <v-checkbox
                  v-else-if="column.column_type === 'checkbox'"
                  v-model="currentData[column.column_name]"
                  :label="column.column_name"
                  :hint="column.help_text"
                  persistent-hint
                ></v-checkbox>

                <!-- Email -->
                <v-text-field
                  v-else-if="column.column_type === 'email'"
                  v-model="currentData[column.column_name]"
                  :label="column.column_name"
                  type="email"
                  :required="column.is_required"
                  :rules="[
                    ...(column.is_required ? [v => !!v || 'Pflichtfeld'] : []),
                    v => !v || /.+@.+\..+/.test(v) || 'Ungültige E-Mail'
                  ]"
                  :hint="column.help_text"
                  persistent-hint
                ></v-text-field>

                <!-- Phone -->
                <v-text-field
                  v-else-if="column.column_type === 'phone'"
                  v-model="currentData[column.column_name]"
                  :label="column.column_name"
                  type="tel"
                  :required="column.is_required"
                  :rules="column.is_required ? [v => !!v || 'Pflichtfeld'] : []"
                  :hint="column.help_text"
                  persistent-hint
                ></v-text-field>

                <!-- URL -->
                <v-text-field
                  v-else-if="column.column_type === 'url'"
                  v-model="currentData[column.column_name]"
                  :label="column.column_name"
                  type="url"
                  :required="column.is_required"
                  :rules="column.is_required ? [v => !!v || 'Pflichtfeld'] : []"
                  :hint="column.help_text"
                  persistent-hint
                ></v-text-field>

                <!-- User (TODO: User-Auswahl-Component) -->
                <v-text-field
                  v-else-if="column.column_type === 'user'"
                  v-model="currentData[column.column_name]"
                  :label="column.column_name"
                  :required="column.is_required"
                  :rules="column.is_required ? [v => !!v || 'Pflichtfeld'] : []"
                  :hint="column.help_text"
                  persistent-hint
                ></v-text-field>

                <!-- File (TODO: File-Upload-Component) -->
                <v-file-input
                  v-else-if="column.column_type === 'file'"
                  v-model="currentData[column.column_name]"
                  :label="column.column_name"
                  :required="column.is_required"
                  :rules="column.is_required ? [v => !!v || 'Pflichtfeld'] : []"
                  :hint="column.help_text"
                  persistent-hint
                ></v-file-input>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dataDialog = false">Abbrechen</v-btn>
          <v-btn
            color="success"
            @click="saveData"
            :loading="saving"
          >
            {{ editingItem ? 'Aktualisieren' : 'Hinzufügen' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import axios from 'axios';
import { API_BASE_URL } from '@/config';

const route = useRoute();

// State
const list = ref(null);
const columns = ref([]);
const data = ref([]);
const permissions = ref({});
const loading = ref(true);
const dataDialog = ref(false);
const editingItem = ref(null);
const currentData = ref({});
const saving = ref(false);
const search = ref('');
const exporting = ref(false);

// Computed
const tableHeaders = computed(() => {
  const headers = columns.value.map(col => ({
    title: col.column_name,
    key: col.column_name,
    sortable: true
  }));

  headers.push({
    title: 'Aktionen',
    key: 'actions',
    sortable: false,
    width: 120
  });

  return headers;
});

const tableData = computed(() => {
  return data.value.map(item => {
    const rowData = { ...item.data };
    rowData._id = item.id;
    rowData._created_by = item.created_by_name;
    rowData._created_at = item.created_at;
    return rowData;
  });
});

// Methods
const loadList = async () => {
  loading.value = true;
  try {
    const response = await axios.get(`${API_BASE_URL}dynamic_lists.php?list_id=${route.params.id}`, {
      withCredentials: true
    });
    list.value = response.data.list;
    columns.value = response.data.columns;
    data.value = response.data.data;
    permissions.value = response.data.permissions;
  } catch (error) {
    console.error('Fehler beim Laden der Liste:', error);
  } finally {
    loading.value = false;
  }
};

const openAddDialog = () => {
  editingItem.value = null;
  currentData.value = {};

  // Default-Werte setzen
  columns.value.forEach(col => {
    if (col.default_value) {
      currentData.value[col.column_name] = col.default_value;
    } else if (col.column_type === 'checkbox') {
      currentData.value[col.column_name] = false;
    } else if (col.column_type === 'multiselect') {
      currentData.value[col.column_name] = [];
    }
  });

  dataDialog.value = true;
};

const editItem = (item) => {
  editingItem.value = item;
  currentData.value = { ...item };
  delete currentData.value._id;
  delete currentData.value._created_by;
  delete currentData.value._created_at;
  dataDialog.value = true;
};

const saveData = async () => {
  saving.value = true;
  try {
    const action = editingItem.value ? 'update_data' : 'add_data';

    const payload = {
      action,
      list_id: route.params.id,
      data: currentData.value
    };

    if (editingItem.value) {
      payload.data_id = editingItem.value._id;
    }

    const response = await axios.post(`${API_BASE_URL}dynamic_lists.php`, payload, {
      withCredentials: true
    });

    if (response.data.status === 'success') {
      dataDialog.value = false;
      await loadList();
    }
  } catch (error) {
    console.error('Fehler beim Speichern:', error);
  } finally {
    saving.value = false;
  }
};

const deleteItem = async (item) => {
  if (!confirm('Eintrag wirklich löschen?')) return;

  try {
    await axios.delete(`${API_BASE_URL}dynamic_lists.php`, {
      data: { data_id: item._id },
      withCredentials: true
    });
    await loadList();
  } catch (error) {
    console.error('Fehler beim Löschen:', error);
  }
};

const exportToExcel = async () => {
  exporting.value = true;
  try {
    // CSV-Export verwenden (funktioniert ohne PhpSpreadsheet)
    const response = await axios.get(`${API_BASE_URL}export_list_csv.php?list_id=${route.params.id}`, {
      responseType: 'blob',
      withCredentials: true
    });

    // Datei herunterladen
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${list.value.name}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    console.error('Fehler beim Export:', error);
  } finally {
    exporting.value = false;
  }
};

const getColumnWidth = (column) => {
  if (column.column_type === 'textarea') return 12;
  if (['checkbox'].includes(column.column_type)) return 6;
  return 6;
};

// Lifecycle
onMounted(() => {
  loadList();
});
</script>

<style scoped>
.list-detail-container {
  max-width: 1600px;
  margin: 0 auto;
}
</style>
